import { Box, Typography, Tabs, Tab } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react'
import { debounce, decodedJWT, getUserRole, getOwnerType, createQueryParam } from '../../utils/commonUtils';
import User from '../../interfaces/user';
import getUserColDef from '../../colDef/usersColumnDef';
import SearchResultGrid from '../searchResultGrid/searchResultGrid';
import { createFilters, getSelectedFiltersCount } from '../../utils/filterUtils';
import Picklist from '../../interfaces/picklist';
import Filter from '../../interfaces/filters';
import FilterDrawer from '../dashboard/filterDrawer';
import { GridPaginationModel } from '@mui/x-data-grid';
import UserService from '../../services/userService';
import Loader from '../baseComponents/loader';
import UserDetailsDrawer from './userDetailsDrawer';
import UsersToolbar from './usersToolbar';
import JWTTokenPayload from '../../interfaces/JWTTokenPayload';
import RoleManagement from '../../security/roleManagement';
import CoverageManagement from '../coverage/coverage';

const filterKeys = ['user_status', 'employment_type', 'username', 'phone_number', 'registration_request_date'];

const ManageUsers: React.FC = () => {
    const token: false | JWTTokenPayload = decodedJWT();
    const userRole = getUserRole(token);
    const ownerType = getOwnerType(token);
    const isAgencyOwner = RoleManagement.isAgency(ownerType);
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [users, setUsers] = useState<User[]>([]);
    const [usersCount, setUsersCount] = useState<number>(0);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 40 });
    const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
    const [filters, setFilters] = useState<Filter[]>([]);
    const [filtersCount, setFiltersCount] = useState<number>(0);
    const [leftMargin, setLeftMargin] = useState<string>('16px');
    const finalColumnDef = getUserColDef(setSelectedUser);

    const setUserRowInGrid = (userRow: User) => {
        const userIndex = users.findIndex(val => val.username === userRow.username);
        if (userIndex !== -1) {
            let usersArray = [...users];
            usersArray[userIndex] = userRow;
            setUsers(usersArray);
        }
    };

    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            const filterString = createQueryParam(filters, '', -1, '', false, []);
            const adminFilter = RoleManagement.isSuperAdmin(userRole) ? 'user_role=Admin' : '';
            const finalFilterString = adminFilter 
                ? (filterString ? `${adminFilter}&${filterString}` : adminFilter)
                : filterString;
            const response = await UserService.getUsers(finalFilterString);
            if (response) {
                setUsers(response.user_responses);
                setUsersCount(response.count);
            }
        } catch (_) {} finally {
            setIsLoading(false);
        }
    };

    const debouncedFunc = useMemo(() => debounce(fetchUsers, 500), [filters, paginationModel]);

    useEffect(() => {
        debouncedFunc.cancel();
        debouncedFunc();
        return () => {
            debouncedFunc.cancel();
        };
    }, [filters, paginationModel]);

    useEffect(() => {
        setFiltersCount(getSelectedFiltersCount(filters));
    }, [filters]);

    const setDefaultFilters = () => {
        const finalFilters = createFilters(
            filterKeys, [], [], [], Picklist.createDefault(), [], [], [], []
        );
        setFilters(finalFilters);
    };

    const toggleFilterDrawer = () => {
        setIsFiltersOpen(!isFiltersOpen);
        setLeftMargin(isFiltersOpen ? "16px" : "166px");
    };

    const closeFilterDrawer = () => {
        if (isFiltersOpen) {
            setIsFiltersOpen(false);
            setLeftMargin("16px");
        }
    };

    const resetFilters = () => {
        setDefaultFilters();
        setPaginationModel({ page: 0, pageSize: 40 });
    };

    // This function will be passed to modals, so that they can trigger reload before successful closing
    const reloadUsers = () => {
        // This will trigger the debounce function, reloading the page, and also bring the user back to first 40 rows
        setPaginationModel({ page: 0, pageSize: 40 });
    };

    useEffect(() => {
        resetFilters();
    }, []);

    // Close filter drawer when switching tabs
    useEffect(() => {
        closeFilterDrawer();
    }, [selectedTab]);

    return (
        <Box sx={{ ml: leftMargin, mt: 2, mr: 2, mb: 2 }}>
            <FilterDrawer
                filters={filters}
                setFilters={setFilters}
                drawerOpen={isFiltersOpen}
                toggleDrawer={toggleFilterDrawer}
            />
            <Typography variant="h5">Manage {RoleManagement.isSuperAdmin(userRole) ? 'Admins' : 'Users'}</Typography>
            
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2, mb: 2 }}>
                {isAgencyOwner && (
                    <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
                        <Tab label="Users" value={0} />
                        <Tab label="Serviceable Territory" value={1} />
                    </Tabs>
                )}
            </Box>

            {selectedTab === 0 && (
                <>
                    <UsersToolbar
                        filtersCount={filtersCount}
                        resetFilters={resetFilters}
                        toggleFilterDrawer={toggleFilterDrawer}
                        reloadFunction={reloadUsers}
                    />
                    {isLoading ? (
                        <Loader color="primary" />
                    ) : (
                        <SearchResultGrid
                            getRowId={(row: User) => row.username}
                            results={users}
                            rowCount={usersCount}
                            onSelectRow={() => {}}
                            columnVisibilityModel={{ 'last_password_updated_date': false }}
                            columns={finalColumnDef}
                            paginationModel={paginationModel}
                            setPaginationModel={setPaginationModel}
                            disableDownloadAsCSVOptions
                            disableDownloadByPrintOptions
                        />
                    )}
                    <UserDetailsDrawer
                        selectedUser={selectedUser}
                        setSelectedUser={setSelectedUser}
                        setUserInGrid={setUserRowInGrid}
                        reloadFunction={reloadUsers}
                    />
                </>
            )}

            {selectedTab === 1 && isAgencyOwner && (
                <CoverageManagement />
            )}
        </Box>
    )
}

export default ManageUsers;